import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Skeleton,
  Alert,
  AlertTitle,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useColors } from 'legacyfrontend/hooks/useColors';

const AccordionErrorState = ({ onRetry, isLoading }) => {
  const { background } = useColors();

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Skeleton height={30} variant='text' width='60%' />
        <Box sx={{ mt: 2 }}>
          <Skeleton height={100} variant='rectangular' width='100%' />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Alert
        icon={<ErrorOutlineIcon fontSize='inherit' />}
        severity='error'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '& .MuiAlert-message': {
            width: '100%',
            textAlign: 'center',
          },
        }}
      >
        <AlertTitle>Error al cargar los datos</AlertTitle>
        <Typography sx={{ mb: 2 }} variant='body2'>
          No pudimos cargar la información en este momento. Por favor, intente nuevamente más tarde.
        </Typography>
        {onRetry && (
          <Button
            color='inherit'
            onClick={onRetry}
            size='small'
            sx={{
              color: background,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Reintentar
          </Button>
        )}
      </Alert>
    </Box>
  );
};

const CustomAccordion = ({
  id,
  title,
  subtitle,
  summarySubtitle,
  children,
  expanded,
  onChange,
  sx = {},
  icon: Icon,
  isLoading = false,
  hasError = false,
  onRetry,
}) => {
  const { background } = useColors();

  return (
    <Accordion
      expanded={expanded}
      id={id}
      onChange={onChange}
      sx={{
        mb: 2,
        borderRadius: '15px !important',
        '&:first-of-type': {
          borderRadius: '15px !important',
        },
        '&:before': {
          display: 'none',
        },
        ...sx,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: background }} />}
        sx={{
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            m: 0,
          },
          minHeight: expanded ? 64 : 90,
          '& .MuiAccordionSummary-content.Mui-expanded': {
            my: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          {Icon && (
            <Icon
              sx={{
                color: background,
              }}
            />
          )}
          <Typography variant='h5'>{title}</Typography>
          {subtitle && !expanded && (
            <Typography color='text.secondary' sx={{ pr: 2, ml: 'auto' }} variant='body1'>
              {subtitle}
            </Typography>
          )}
        </Box>
        {!subtitle && summarySubtitle && !expanded && (
          <Typography sx={{ pr: 2, ml: 'auto', minWidth: 200, textAlign: 'right' }} variant='h6'>
            {summarySubtitle}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {isLoading || hasError ? (
          <AccordionErrorState isLoading={isLoading} onRetry={hasError ? onRetry : undefined} />
        ) : (
          children
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CustomAccordion;
